import { useState, useMemo, useCallback } from 'react'

import * as yup from 'yup'

import {
  hasUploadValue,
  normalizeUploadSubmitValue
} from 'utils/customFieldUpload'

import { ExtraFieldItem } from './index'

interface UseExtraFieldsProps {
  initialItems?: ExtraFieldItem[]
  baseSchema?: yup.ObjectSchema<any>
  excludeFieldTypes?: string[]
}

export const useExtraFields = ({
  initialItems = [],
  baseSchema,
  excludeFieldTypes = []
}: UseExtraFieldsProps = {}) => {
  const [items, setItems] = useState<ExtraFieldItem[]>(initialItems)

  const handleItemsChange = useCallback(
    (newItems: ExtraFieldItem[]) => {
      setItems(newItems)
    },
    [items]
  )

  const dynamicSchema = useMemo(() => {
    if (!baseSchema) return undefined

    const extraFieldsSchema = items
      .filter((field) => {
        const fieldType = String(
          field.extra_fieldable_type || field.field_type || 'short_text'
        )
        return !excludeFieldTypes.includes(fieldType)
      })
      .reduce((acc, field) => {
        const fieldName = `extraField_${field.uuid}`

        if (field.required) {
          switch (field.extra_fieldable_type || field.field_type) {
            case 'text':
            case 'short_text':
            case 'long_text':
            case 0:
            default:
              acc[fieldName] = yup
                .string()
                .nullable()
                .required(`Campo obrigatório`)
              break
            case 'radio':
            case 1:
              acc[fieldName] = yup
                .string()
                .nullable()
                .required(`Campo obrigatório`)
              break
            case 'checkbox':
            case 2:
              acc[fieldName] = yup
                .array()
                .nullable()
                .min(1, `Selecione pelo menos uma opção para ${field.name}`)
                .required(`Campo obrigatório`)
              break
            case 'select':
              acc[fieldName] = yup
                .string()
                .nullable()
                .required(`Campo obrigatório`)
              break
            case 'accept':
              acc[fieldName] = yup
                .boolean()
                .nullable()
                .oneOf([true], `Campo obrigatório`)
              break
            case 'upload':
            case 4:
              // Valor é o uuid do arquivo ou a lista de uuids (vários
              // arquivos): basta ter pelo menos 1
              acc[fieldName] = yup
                .mixed()
                .nullable()
                .test('upload-required', 'Campo obrigatório', (value) =>
                  hasUploadValue(value)
                )
              break
            case 'date':
            case 6:
              acc[fieldName] = yup
                .string()
                .nullable()
                .required(`Campo obrigatório`)
              break
          }
        } else {
          // Campos opcionais
          switch (field.extra_fieldable_type || field.field_type) {
            case 'text':
            case 'short_text':
            case 'long_text':
            case 0:
            default:
              acc[fieldName] = yup.string().nullable().optional()
              break
            case 'radio':
            case 1:
              acc[fieldName] = yup.string().nullable().optional()
              break
            case 'checkbox':
            case 2:
              acc[fieldName] = yup.array().nullable().optional()
              break
            case 'select':
              acc[fieldName] = yup.string().nullable().optional()
              break
            case 'accept':
              // Campos de aceite são sempre obrigatórios, independente da configuração
              acc[fieldName] = yup
                .boolean()
                .nullable()
                .oneOf([true], `Campo obrigatório`)
              break
            case 'upload':
            case 4:
              acc[fieldName] = yup.mixed().nullable().optional()
              break
            case 'date':
            case 6:
              acc[fieldName] = yup.string().nullable().optional()
              break
          }
        }

        return acc
      }, {} as Record<string, any>)

    return baseSchema.shape(extraFieldsSchema)
  }, [items, baseSchema])

  const getExtraFieldsData = useCallback(
    (formData: Record<string, any>) => {
      const extraFieldsData: Record<string, any> = {}

      items.forEach((field) => {
        const fieldName = `extraField_${field.uuid}`
        const value = formData[fieldName]

        if (value !== undefined && value !== null && value !== '') {
          extraFieldsData[field.uuid] = {
            value,
            field_type: field.extra_fieldable_type || field.field_type,
            name: field.name
          }
        }
      })

      return extraFieldsData
    },
    [items]
  )

  const setInitialValues = useCallback(
    (setValue: any, extraFieldsData?: Record<string, any>) => {
      items.forEach((field) => {
        const fieldName = `extraField_${field.uuid}`
        const fieldType = String(
          field.extra_fieldable_type || field.field_type || 'short_text'
        )

        let defaultValue: any

        // Define valores padrão baseados no tipo do campo
        switch (fieldType) {
          case 'checkbox':
          case '2':
            defaultValue = []
            break
          case 'accept':
            defaultValue = false
            break
          case 'upload':
          case '4':
            defaultValue = null
            break
          default:
            defaultValue = ''
            break
        }

        if (extraFieldsData && extraFieldsData[field.uuid]) {
          const fieldData = extraFieldsData[field.uuid]
          if (fieldData.value !== undefined && fieldData.value !== null) {
            setValue(fieldName, fieldData.value)
          } else {
            setValue(fieldName, defaultValue)
          }
        } else {
          setValue(fieldName, defaultValue)
        }
      })
    },
    [items]
  )

  const getCustomFieldValues = useCallback(
    (formData: Record<string, any>) => {
      const customFieldValues: Array<{
        custom_field_uuid: string
        value: any
      }> = []

      items.forEach((field) => {
        const fieldName = `extraField_${field.uuid}`
        const fieldValue = formData[fieldName]

        let processedValue = fieldValue

        const fieldType = String(
          field.extra_fieldable_type || field.field_type || 'short_text'
        )

        if (fieldType === 'checkbox' || fieldType === '2') {
          if (Array.isArray(fieldValue) && fieldValue.length > 0) {
            processedValue = fieldValue.map((value) => {
              const option = field.options?.find((opt) => opt.value === value)
              return option
                ? { label: option.label, value: option.value }
                : { label: value, value }
            })
          } else {
            processedValue = []
          }
        } else if (fieldType === 'radio' || fieldType === '1') {
          if (fieldValue) {
            const option = field.options?.find(
              (opt) => opt.value === fieldValue
            )
            processedValue = option
              ? { label: option.label, value: option.value }
              : String(fieldValue)
          } else {
            processedValue = ''
          }
        } else if (fieldType === 'accept') {
          processedValue = Boolean(fieldValue)
        } else if (fieldType === 'upload' || fieldType === '4') {
          processedValue = normalizeUploadSubmitValue(fieldValue)
        } else {
          processedValue = fieldValue ? String(fieldValue) : ''
        }
        customFieldValues.push({
          custom_field_uuid: field.uuid,
          value: processedValue
        })
      })

      return customFieldValues
    },
    [items]
  )

  return {
    items,
    setItems,
    handleItemsChange,
    dynamicSchema,
    getExtraFieldsData,
    setInitialValues,
    getCustomFieldValues
  }
}
